import type { ITelemetryItem } from '@microsoft/applicationinsights-web';

const urlLikePrefix = /^(?:(?:https?|wss?):\/\/|\/\/|\/|\.{1,2}\/|[?#])/i;
const bareRelativeUrl = /^[^\s?#]+[?#][^\s]*$/;
const httpDependencyName = /^(?:CONNECT|DELETE|GET|HEAD|OPTIONS|PATCH|POST|PUT|TRACE)\s+(.+)$/i;

const isUrlLike = (value: string): boolean => {
  const dependencyTarget = httpDependencyName.exec(value)?.[1] ?? value;
  return urlLikePrefix.test(dependencyTarget) || bareRelativeUrl.test(dependencyTarget);
};

/**
 * Removes the complete query and fragment without parsing, decoding, hashing, or
 * partially retaining either value. This works for absolute URLs, relative routes,
 * query-only values, and HTTP dependency names such as `POST /bff/validate?...`.
 *
 * @param value Telemetry string that may contain a URL or route.
 * @returns The path-only value when URL-like, otherwise the original string.
 */
export const stripTelemetryUrlQueryAndFragment = (value: string): string => {
  if (!isUrlLike(value)) return value;

  const queryIndex = value.indexOf('?');
  const fragmentIndex = value.indexOf('#');
  const queryBoundary = queryIndex === -1 ? value.length : queryIndex;
  const fragmentBoundary = fragmentIndex === -1 ? value.length : fragmentIndex;

  return value.slice(0, Math.min(queryBoundary, fragmentBoundary));
};

type TelemetryObject = Record<string, unknown> | unknown[];

const sanitiseTelemetryValue = (value: unknown, visited: WeakSet<TelemetryObject>): unknown => {
  if (typeof value === 'string') return stripTelemetryUrlQueryAndFragment(value);
  if (value === null || typeof value !== 'object') return value;

  const telemetryObject = value as TelemetryObject;
  if (visited.has(telemetryObject)) return value;

  visited.add(telemetryObject);

  if (Array.isArray(value)) {
    value.forEach((item, index) => {
      value[index] = sanitiseTelemetryValue(item, visited);
    });
    return value;
  }

  const record = value as Record<string, unknown>;
  Object.keys(record).forEach((key) => {
    record[key] = sanitiseTelemetryValue(record[key], visited);
  });

  return value;
};

/**
 * Applies the browser telemetry boundary to every envelope value before the SDK
 * sends it. Walking the whole item also covers SDK/custom properties that may copy
 * the current page or dependency URL under a different field name.
 *
 * @param envelope Mutable Application Insights telemetry envelope.
 * @param roleName Stable cloud role included in the sanitised envelope.
 * @returns `true` to retain a fully sanitised item, or `false` to drop it when
 * sanitisation cannot complete.
 */
export const applyBrowserTelemetryHygiene = (
  envelope: ITelemetryItem,
  roleName: string,
): boolean => {
  try {
    if (!envelope.tags) envelope.tags = {};
    envelope.tags['ai.cloud.role'] = roleName;
    sanitiseTelemetryValue(envelope, new WeakSet<TelemetryObject>());
    return true;
  } catch {
    // Privacy fails closed: discard an item that cannot be fully sanitised without
    // logging either the telemetry item or the sanitisation error.
    return false;
  }
};
